import { MapsAPILoader } from '@agm/core';
import {} from '@angular/google-maps';
import {
  AfterViewInit,
  Component,
  ElementRef,
  EventEmitter,
  Injector,
  Input,
  NgZone,
  Output,
  ViewChild,
} from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { environment } from 'src/environments/environment';
import { BsNgInputBaseComponent } from '../BsNgInputTextBaseComponent';
import { MapLocation } from '../../../../../core/models/shared/MapLocation';
import { NgControl } from '@angular/forms';
import { MakeProvider } from '../AbstractValueAccessor';

@Component({
  selector: 'bs-ng-map',
  templateUrl: './bs-ng-map.component.html',
  providers: [MakeProvider(BsNgMapComponent)],
})
export class BsNgMapComponent
  extends BsNgInputBaseComponent
  implements AfterViewInit
{
  @Input()
  public markerDraggable = true;

  @ViewChild('search')
  searchElementRef: ElementRef;

  @Input()
  showValueDisplay = true;

  @Input()
  notSelectedLocationText = 'Not Selected';

  @Output() locationChanged = new EventEmitter();

  ///represent what the map is showing, rather than the value
  public address: string;
  public latitude: number;
  public longitude: number;

  public zoom: number;
  public radius: number;
  public circleColor: string;
  private geoCoder: any;

  public currentLatitude: number;
  public currentLongitude: number;

  @Input()
  public loadUserLocation: boolean;

  private get isDefaultLocation(): boolean {
    if (
      this.longitude == environment.googleMapConfigs.defaultLongitude &&
      this.latitude == environment.googleMapConfigs.defaultLatitude
    ) {
      return true;
    }

    return false;
  }

  get value() {
    return super.value;
  }

  set value(v: any) {
    const defaultLocation =
      this.getLoadedCurrentLocation() || this.createDefaultLocation();

    if (v) {
      this.address = v.address;

      ///there is a value entered but not valid location numbers
      if (
        v.latitude == null ||
        v.longitude == null ||
        isNaN(v.latitude) ||
        isNaN(v.longitude)
      ) {
        console.warn(`not a valid values for location`, v);
        this.latitude = defaultLocation.latitude;
        this.longitude = defaultLocation.longitude;
      } else {
        this.latitude = Number(v.latitude);
        this.longitude = Number(v.longitude);
      }
    } else {
      console.warn(
        `${v} is not a valid value for the map, default location used`
      );

      this.address = defaultLocation.address;
      this.longitude = defaultLocation.longitude;
      this.latitude = defaultLocation.latitude;
    }

    super.value = v;
  }

  writeValue(value: any) {
    this.value = value;
  }

  public get gestureHandling(): 'cooperative' | 'greedy' | 'none' | 'auto' {
    if (this.ngControl.disabled) {
      return 'none';
    }

    return 'auto'; //default
  }

  public get disableDefaultUI(): boolean {
    if (this.ngControl.disabled) {
      return true;
    }

    return false; //default
  }

  public get showAddressTextSearch(): boolean {
    if (this.ngControl.disabled) {
      return false;
    }

    return true; //default
  }

  public get isShowHelpText(): boolean {
    if (this.ngControl.disabled) {
      return false;
    }

    return true; //default
  }

  public get valueLatitude(): string {
    return this.value && this.value.latitude
      ? this.value.latitude
      : this.notSelectedLocationText;
  }

  public get valueLongitude(): string {
    return this.value && this.value.longitude
      ? this.value.longitude
      : this.notSelectedLocationText;
  }

  constructor(
    translate: TranslateService,
    injector: Injector,
    private mapsApiLoader: MapsAPILoader,
    private ngZone: NgZone
  ) {
    super(translate, injector);

    ///this will help loading the default location on the map
    this.value = this.createDefaultLocation();

    this.zoom = environment.googleMapConfigs.defaultZoom;
    this.radius = environment.googleMapConfigs.defaultRadius;
    this.circleColor = environment.googleMapConfigs.defaultCircleColor;

    this.loadGoogleApis();
  }

  ngOnInit() {
    if (this.key !== '') {
      this.getTranslations();
    }

    this.ngControl = this.inj.get(NgControl);

    this.loadCurrentLocation();
  }

  ngAfterViewInit() {
    //this.subscribeToAutoCompleteChange();

    if (this.loadUserLocation && this.isDefaultLocation) {
      this.setMapToCurrentLocation();
    }
  }

  // ///used for reactive forms
  // public override setDisabledState(isDisabled: boolean) {
  //   super.setDisabledState(isDisabled);

  //   if (isDisabled == false) {
  //     ///#search get remove from dom on disabled map, hence re-register the events
  //     this.subscribeToAutoCompleteChange();
  //   }
  // }

  private async loadGoogleApis() {
    await this.mapsApiLoader
      .load()
      .then(() => this.subscribeToAutoCompleteChange());
    this.geoCoder = new google.maps.Geocoder();
  }

  private loadCurrentLocation(): Promise<void> {
    return new Promise((resolve, reject) => {
      if ('geolocation' in navigator) {
        navigator.geolocation.getCurrentPosition(
          (position) => {
            this.currentLatitude = position.coords.latitude;
            this.currentLongitude = position.coords.longitude;

            resolve();
          },
          (error) => {
            reject(error);
          }
        );
      } else {
        reject(
          'geolocation is not exist, enable the browser navigation service'
        );
      }
    });
  }

  private async subscribeToAutoCompleteChange() {
    let autocomplete = new google.maps.places.Autocomplete(
      this.searchElementRef?.nativeElement
    );

    autocomplete.addListener('place_changed', () => {
      this.ngZone.run(() => {
        //get the place result
        let place: google.maps.places.PlaceResult = autocomplete.getPlace();
        //verify result
        if (place.geometry === undefined || place.geometry === null) {
          return;
        }
        this.setLocation(
          place.geometry.location.lat(),
          place.geometry.location.lng()
        ).then(() => {
          this.locationChanged.emit(this.value);
        });
      });
    });
  }

  private getLoadedCurrentLocation(): MapLocation {
    if (this.currentLongitude && this.currentLatitude) {
      return {
        longitude: this.currentLongitude,
        latitude: this.currentLatitude,
        address: '',
      };
    } else {
      return null;
    }
  }

  private async setMapToCurrentLocation() {
    await this.loadCurrentLocation();

    this.longitude = this.currentLongitude;
    this.latitude = this.currentLatitude;
  }

  public async markerDragEnd($event: any) {
    await this.setLocation($event.coords.lat, $event.coords.lng).then(() => {
      this.locationChanged.emit(this.value);
    });
  }

  private async setLocation(latitude: number, longitude: number) {
    await this.getAddress(this.latitude, this.longitude).then((address) => {
      this.value = {
        latitude: latitude,
        longitude: longitude,
        address: address,
      };
    });
  }

  private async getAddress(
    latitude: number,
    longitude: number
  ): Promise<string> {
    try {
      const result: any = await new Promise((resolve, reject) => {
        this.geoCoder.geocode(
          { location: { lat: latitude, lng: longitude } },
          (results: any, status: any) => {
            if (status === 'OK' && results) {
              resolve({ results, status });
            } else {
              reject(new Error(`Geocoding failed: ${status}`));
            }
          }
        );
      });
      if (result.results[0]) {
        return result.results[0].formatted_address;
      }
      return null;
    } catch (error) {
      console.error('Error getting address:', error);
      return null;
    }
  }

  public get touched() {
    return this.ngControl.control.touched;
  }

  public async addressTextChange() {
    if (this.address == '') {
      this.value = null;
    }
  }

  private createDefaultLocation(): MapLocation {
    const location = new MapLocation();
    location.latitude = environment.googleMapConfigs.defaultLatitude;
    location.longitude = environment.googleMapConfigs.defaultLongitude;

    return location;
  }
}
